import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import api from 'api/axios/foka'
import { createAxiosThunk } from 'utils/reduxUtil'
import moment from 'moment'
import { orderBy } from 'lodash'
import { sanitize } from 'features/report/reportApiUtils'
import { AD_ACCOUNT, CAMPAIGN } from 'features/report/common/DomainText'
import {
  CALENDAR_PRESETS,
  findPresetByProtocol,
  fmt,
  getPresetRange,
} from 'components/calendar/calendarUtil'
import { isNullOrUndefined } from '../../utils'
import { Toasts } from '../toast/toastSlice'
import { CampaignTypes } from '../Codes'

export const backwardDeleted = (items = []) => {
  const notDeleted = items.filter(({ deletedDate }) =>
    isNullOrUndefined(deletedDate)
  )
  const deleted = items.filter(
    ({ deletedDate }) => !isNullOrUndefined(deletedDate)
  )
  return [...notDeleted, ...deleted]
}
const orderByHour = splits => orderBy(splits, ['dateId', 'hr'], ['asc', 'asc'])

const initialState = {
  reports: [],
  unfold: [],
  page: 0,
  size: 10,
  total: 0,
  isLoading: false,
  isFilterLoading: false,
  loadingError: undefined,
  apiError: undefined,
  formValues: {
    calendarPreset: CALENDAR_PRESETS.LAST_7_DAYS,
    period: {
      specific: {
        start: moment().add(-6, 'day').format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD'),
      },
      relative: 'last7Days',
    },
    domain: AD_ACCOUNT,
    entity: AD_ACCOUNT,
    reportId: undefined,
    domainStatuses: ['ON', 'OFF', 'DELETED'],
    campaignType: undefined,
    campaignId: undefined,
    adGroupId: undefined, // undefined: 없음, '': 전체
    metricsTypes: ['defaults'],
    analysisItem: 'none',
    analysisPeriod: 'day',
    name: '',
  },
  campaigns: [],
  groups: [],
}

export const addReport = createAxiosThunk(
  'reportAdd/createReport',
  (noArgs, { getState }) =>
    api.report.addReport(getState().report.reportAdd.formValues)
)

export const modifyReport = createAxiosThunk(
  'reportAdd/modifyReport',
  (reportId, { getState }) =>
    api.report.modifyReport({
      customReportId: reportId,
      param: getState().report.reportAdd.formValues,
    })
)

export const fetchReports = createAxiosThunk(
  'reportAdd/fetchReports',
  (noArgs, { getState }) => {
    const {
      page,
      size,
      formValues,
      formValues: { domain },
    } = getState().report.reportAdd
    return api.report.getDomainReports({
      page,
      size,
      domain,
      param: sanitize(formValues),
    })
  }
)

export const fetchCampaigns = createAsyncThunk(
  'reportAdd/fetchCampaigns',
  async (noArgs, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.ad.getCampaigns()
      return data
    } catch (error) {
      dispatch(Toasts.fail('캠페인을 불러오는데 실패하였습니다.'))
      return rejectWithValue(error.response.data)
    }
  }
)

export const fetchAdGroups = createAsyncThunk(
  'reportAdd/fetchAdGroups',
  async (noArgs, { dispatch, getState, rejectWithValue }) => {
    try {
      const { campaignId } = getState().report.reportAdd.formValues
      const { data } = await api.ad.getCampaignAdGroups(campaignId)
      return data
    } catch (error) {
      dispatch(Toasts.fail('광고그룹을 불러오는데 실패하였습니다.'))
      return rejectWithValue(error.response.data)
    }
  }
)

const reportAddSlice = createSlice({
  name: 'reportAdd',
  initialState,

  reducers: {
    reset: () => initialState,
    setFormValues: (state, { payload }) => {
      state.formValues = payload
      const protocol = payload?.period?.relative
      const calendarPreset = findPresetByProtocol(protocol)
      state.formValues.calendarPreset = calendarPreset
      if (calendarPreset !== CALENDAR_PRESETS.CUSTOM) {
        const { start, end } = getPresetRange(
          calendarPreset,
          moment().subtract(2, 'year'),
          moment()
        )
        state.formValues.period.specific = {
          start: start.format(fmt),
          end: end.format(fmt),
        }
      }
      state.formValues.domain = payload.entity
    },
    setUnfold: (state, { payload }) => {
      state.unfold = payload
    },
    setLoading: (state, { payload }) => {
      state.isLoading = payload
    },
    setRange: (state, { payload: { start, end, preset } }) => {
      state.formValues.period = {
        specific: {
          start,
          end,
        },
        relative: preset.protocol,
      }
      state.formValues.calendarPreset = preset
      const days = moment(end).diff(moment(start), 'days')
      const months = moment(end).diff(moment(start), 'months')
      const { analysisPeriod } = state.formValues
      if (
        preset.protocol === 'all' ||
        (days > 30 && analysisPeriod === 'day') ||
        (days > 61 && analysisPeriod === 'week') ||
        (months > 12 && analysisPeriod === 'month')
      ) {
        state.formValues.analysisPeriod = 'none'
        if (state.formValues.analysisItem === 'hour') {
          state.formValues.analysisItem = 'none'
        }
      } else if (
        moment().diff(moment(start), 'days') > 89 &&
        state.formValues.analysisItem === 'hour'
      ) {
        state.formValues.analysisItem = 'none'
      }
    },
    setDomain: (state, { payload: domain }) => {
      state.reports = []
      state.formValues.domain = domain
      state.formValues.entity = domain // 보고서 생성 API는 파라미터명이 entity
      state.formValues.metricsTypes = ['defaults']
      state.formValues.analysisItem = 'none'
      state.formValues.campaignType = undefined
      state.formValues.campaignId = undefined
      state.formValues.adGroupId = undefined
      state.formValues.calendarPreset = findPresetByProtocol('last7Days')
      state.formValues.period = {
        specific: {
          start: moment().add(-6, 'day').format('YYYY-MM-DD'),
          end: moment().format('YYYY-MM-DD'),
        },
        relative: 'last7Days',
      }
    },
    setDomainStatuses: (state, { payload: status }) => {
      const idx = state.formValues.domainStatuses.indexOf(status)
      if (idx > -1) {
        if (state.formValues.domainStatuses.length > 1) {
          // 전체 해제 불가
          state.formValues.domainStatuses.splice(idx, 1)
        }
      } else {
        state.formValues.domainStatuses.push(status)
      }
    },
    setMetricsTypes: (state, { payload: metric }) => {
      const idx = state.formValues.metricsTypes.indexOf(metric)
      if (idx > -1) {
        if (state.formValues.metricsTypes.length > 1) {
          // 전체 해제 불가
          state.formValues.metricsTypes.splice(idx, 1)
        }
      } else {
        state.formValues.metricsTypes.push(metric)
      }
    },
    setAnalysisItem: (state, { payload: analysisItem }) => {
      state.formValues.analysisItem = analysisItem
    },
    setAnalysisPeriod: (state, { payload: analysisPeriod }) => {
      state.formValues.analysisPeriod = analysisPeriod
    },
    setReportName: (state, { payload: name }) => {
      state.formValues.name = name
    },
    setPage: (state, { payload: page }) => {
      state.page = page
    },
    setReports: (state, { payload: reports }) => {
      if (Array.isArray(reports.content)) {
        state.total = reports.totalElements
        state.reports = reports.content
      } else {
        state.total = 1
        state.reports = [reports]
      }
    },
    setCampaignType: (state, { payload: campaignType }) => {
      state.formValues.campaignType = campaignType
      state.formValues.campaignId = undefined
      state.formValues.adGroupId = undefined
      if (
        state.domain !== CAMPAIGN &&
        campaignType !== CampaignTypes.TALK_CHANNEL
      ) {
        const index = state.formValues.metricsTypes.indexOf('kakaoFriend')
        if (index !== -1) state.formValues.metricsTypes.splice(index, 1)
      }
    },
    setCampaignId: (state, { payload: id }) => {
      state.formValues.campaignId = id
      state.formValues.adGroupId = undefined
      state.formValues.analysisItem = 'none'
      const days = moment(state.formValues.period.specific.end).diff(
        moment(state.formValues.period.specific.start),
        'days'
      )
      if (
        state.formValues.period.relative === CALENDAR_PRESETS.ALL.protocol ||
        days > 30
      ) {
        state.formValues.analysisPeriod = 'none'
      } else {
        state.formValues.analysisPeriod = 'day'
      }
      state.formValues.metricsTypes = ['defaults']
    },
    setAdGroupId: (state, { payload: id }) => {
      state.formValues.adGroupId = id
    },
  },

  extraReducers: builder => {
    builder.addCase(addReport.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(addReport.pending, state => {
      state.isLoading = true
    })

    builder.addCase(addReport.rejected, (state, { error, payload }) => {
      state.apiError = payload
      state.loadingError = error
      state.isLoading = false
    })

    builder.addCase(modifyReport.fulfilled, state => {
      state.isLoading = false
    })

    builder.addCase(modifyReport.pending, state => {
      state.isLoading = true
    })

    builder.addCase(modifyReport.rejected, (state, { error, payload }) => {
      state.apiError = payload
      state.loadingError = error
      state.isLoading = false
    })

    builder.addCase(fetchReports.fulfilled, (state, { payload }) => {
      state.isLoading = false
      state.unfold = []
      if (payload.result.content) {
        state.reports = payload.result.content
        state.total = payload.result.totalElements
      } else {
        // 광고계정 보고서만 응답이 Array 아님
        const accountResult = payload.result
        if (accountResult.splits) {
          accountResult.splits = orderByHour(accountResult.splits)
        }
        state.reports = [accountResult]
        state.total = 1
      }
    })

    builder.addCase(fetchReports.pending, state => {
      state.isLoading = true
    })

    builder.addCase(fetchReports.rejected, (state, { error }) => {
      state.loadingError = error
      state.isLoading = false
    })

    builder.addCase(
      fetchCampaigns.fulfilled,
      (state, { payload: { campaigns } }) => {
        state.campaigns = backwardDeleted(campaigns)
        const campaign = campaigns.find(
          ({ id }) => id === state.formValues.campaignId
        )
        if (campaign) {
          state.formValues.campaignType = campaign.campaignType
        }
      }
    )

    builder.addCase(
      fetchAdGroups.fulfilled,
      (state, { payload: { adGroupDtos } }) => {
        state.groups = backwardDeleted(adGroupDtos)
        const adGroup = adGroupDtos.find(
          ({ id }) => id === state.formValues.adGroupId
        )
        if (adGroup) {
          state.formValues.campaignType = adGroup.adGroupType
        }
        state.isFilterLoading = false
      }
    )

    builder.addCase(fetchAdGroups.pending, state => {
      state.isFilterLoading = true
    })

    builder.addCase(fetchAdGroups.rejected, state => {
      state.isFilterLoading = false
    })
  },
})

export const {
  reset,
  setLoading,
  setFormValues,
  setUnfold,
  setRange,
  setDomain,
  setDomainStatuses,
  setMetricsTypes,
  setAnalysisItem,
  setAnalysisPeriod,
  setReportName,
  setPage,
  setReports,
  setCampaignType,
  setCampaignId,
  setAdGroupId,
} = reportAddSlice.actions
export default reportAddSlice.reducer
